let watching = false;
let released = false;
let listeners = 0;

const onNativeDrop = () => {
  if (watching) released = true;
};

/**
 * Installs the native `drop` listener that the release watch reads, and returns
 * its teardown. Call once per view, on mount.
 *
 * On mount, and not when a drag starts, because that is the only way to get
 * ahead of pragmatic: it binds its own window-level `drop` listener — capture
 * phase, same node — from inside its `dragstart` handler, and only then
 * schedules `onDragStart` a frame later. A listener armed from `onDragStart` is
 * therefore always the second one registered, so pragmatic's runs first, and
 * pragmatic's calls `onDrop` synchronously — which asks `wasReleased()` before
 * the later listener has seen the event at all. Registering at mount puts this
 * one ahead of every listener a drag can bind.
 *
 * Reference counted, so views that overlap for a render do not tear down each
 * other's listener.
 */
export const installDragReleaseListener = (): (() => void) => {
  if (listeners === 0) {
    window.addEventListener('drop', onNativeDrop, { capture: true });
  }
  listeners += 1;

  let removed = false;
  return () => {
    if (removed) return;
    removed = true;
    listeners -= 1;
    if (listeners === 0) {
      window.removeEventListener('drop', onNativeDrop, { capture: true });
    }
  };
};

/**
 * Arms the watch for the drag that is starting: from here until `wasReleased`,
 * a native `drop` counts as this drag's.
 *
 * Keyed off the native `drop` event rather than the Escape key: during a native
 * HTML5 drag the browser keeps the keyboard to itself and delivers no `keydown`
 * to the page at all (pragmatic itself listens for none, and cancels purely on
 * `dragend`). A release, on the other hand, always produces a `drop` on these
 * boards — they accept every drag document-wide, calling `preventDefault()` on
 * `dragenter`/`dragover` so the cursor never shows the "forbidden" state, which
 * makes the whole document a valid drop surface. Escape produces no `drop`,
 * only `dragend`.
 */
export const watchForDragRelease = () => {
  watching = true;
  released = false;
};

/**
 * Whether the drag that just ended ended in a release rather than a cancel, and
 * disarms the watch. Call once per drop, on every path, so a later `drop` — one
 * belonging to some other drag, or to a nested adapter — cannot be attributed
 * to this one.
 *
 * Clears the flag as it reads it, so a drag whose start returned early — before
 * `watchForDragRelease` ran — cannot be handed the previous drag's answer.
 */
export const wasReleased = (): boolean => {
  watching = false;
  const wasIt = released;
  released = false;
  return wasIt;
};
